import { animate, type AnimationPlaybackControls } from 'framer-motion';

const CHAR_DELETE_SPEED = 30;
const CHAR_TYPE_SPEED = 50;
const PAUSE_BEFORE_TYPE = 150;

type AnimationControlsRef = {
  current: AnimationPlaybackControls | undefined;
};

type DisplayTextRef = {
  current: string;
};

type SetDisplayText = (displayText: string) => void;
type SetIsAnimating = (isAnimating: boolean) => void;

interface TextDisplayState {
  displayTextRef: DisplayTextRef;
  setDisplayText: SetDisplayText;
}

interface DisplayTextUpdateParams extends TextDisplayState {
  latest: number;
  sourceText: string;
}

interface TypingAnimationParams extends TextDisplayState {
  controlsRef: AnimationControlsRef;
  endText: string;
  setIsAnimating: SetIsAnimating;
  typeSteps: number;
}

export interface HackerHeaderTextTransitionParams extends TextDisplayState {
  controlsRef: AnimationControlsRef;
  endText: string;
  setIsAnimating: SetIsAnimating;
  startText: string;
}

export function stopHackerHeaderAnimation(controlsRef: AnimationControlsRef): void {
  if (!controlsRef.current) {
    return;
  }

  controlsRef.current.stop();
}

export function runHackerHeaderTextTransition({
  controlsRef,
  displayTextRef,
  endText,
  setDisplayText,
  setIsAnimating,
  startText,
}: HackerHeaderTextTransitionParams): void {
  setIsAnimating(true);

  const deleteSteps = startText.length;
  const typeSteps = endText.length;
  const deleteAnimation = animate(deleteSteps, 0, {
    duration: deleteSteps * (CHAR_DELETE_SPEED / 1000),
    ease: "linear",
    onUpdate: (latest) => {
      updateDisplayText({
        displayTextRef,
        latest,
        setDisplayText,
        sourceText: startText,
      });
    },
    onComplete: () => {
      setTimeout(() => {
        startTypingAnimation({
          controlsRef,
          displayTextRef,
          endText,
          setDisplayText,
          setIsAnimating,
          typeSteps,
        });
      }, PAUSE_BEFORE_TYPE);
    },
  });

  controlsRef.current = deleteAnimation;
}

function startTypingAnimation({
  controlsRef,
  displayTextRef,
  endText,
  setDisplayText,
  setIsAnimating,
  typeSteps,
}: TypingAnimationParams): void {
  const typeAnimation = animate(0, typeSteps, {
    duration: typeSteps * (CHAR_TYPE_SPEED / 1000),
    ease: "linear",
    onUpdate: (latest) => {
      updateDisplayText({
        displayTextRef,
        latest,
        setDisplayText,
        sourceText: endText,
      });
    },
    onComplete: () => {
      setIsAnimating(false);
      setDisplayText(endText);
      displayTextRef.current = endText;
    },
  });

  controlsRef.current = typeAnimation;
}

function updateDisplayText({
  displayTextRef,
  latest,
  setDisplayText,
  sourceText,
}: DisplayTextUpdateParams): void {
  const length = Math.round(latest);
  const displayText = sourceText.slice(0, length);

  setDisplayText(displayText);
  displayTextRef.current = displayText;
}
